import React, { useState, useMemo } from 'react';
import { Search, Info, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Separator } from '@/components/ui/separator';
import type { InsuranceWithRate } from '@/types/insurance';

interface InsuranceSelectorProps {
  insurances: InsuranceWithRate[];
  selectedInsurances: InsuranceWithRate[];
  onInsuranceChange: (insurances: InsuranceWithRate[]) => void;
  employeeAge?: number;
  className?: string;
}

export const InsuranceSelector: React.FC<InsuranceSelectorProps> = ({
  insurances,
  selectedInsurances,
  onInsuranceChange,
  employeeAge,
  className
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Group insurances by category
  const groupedInsurances = useMemo(() => {
    const filtered = insurances.filter(insurance =>
      insurance.name_german.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insurance.name_english.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insurance.code.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return filtered.reduce((groups, insurance) => {
      const category = insurance.category;
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(insurance);
      return groups;
    }, {} as Record<string, InsuranceWithRate[]>);
  }, [insurances, searchTerm]);

  const categoryLabels = {
    social: 'Sozialversicherungen',
    unemployment: 'Arbeitslosenversicherung',
    pension: 'Berufliche Vorsorge',
    accident: 'Unfallversicherung',
    sickness: 'Krankentaggeld'
  };

  const handleInsuranceToggle = (insurance: InsuranceWithRate, enabled: boolean) => {
    const existingIndex = selectedInsurances.findIndex(s => s.id === insurance.id);
    let newSelection = [...selectedInsurances];

    if (enabled) {
      if (existingIndex === -1) {
        newSelection.push({ ...insurance, is_enabled: true });
      } else {
        newSelection[existingIndex] = { ...newSelection[existingIndex], is_enabled: true };
      }
    } else {
      if (existingIndex !== -1) {
        newSelection[existingIndex] = { ...newSelection[existingIndex], is_enabled: false };
      }
    }

    onInsuranceChange(newSelection);
  };

  const handleRateChange = (insurance: InsuranceWithRate, newRate: number) => {
    const existingIndex = selectedInsurances.findIndex(s => s.id === insurance.id);
    let newSelection = [...selectedInsurances];

    if (existingIndex !== -1) {
      newSelection[existingIndex] = {
        ...newSelection[existingIndex],
        rate_percent: newRate,
        is_custom_rate: newRate !== insurance.rate_percent
      };
    } else {
      newSelection.push({
        ...insurance,
        rate_percent: newRate,
        is_enabled: true,
        is_custom_rate: newRate !== insurance.rate_percent
      });
    }

    onInsuranceChange(newSelection);
  };

  const getSelectedInsurance = (insurance: InsuranceWithRate) => {
    return selectedInsurances.find(s => s.id === insurance.id) || insurance;
  };

  const isInsuranceEnabled = (insurance: InsuranceWithRate) => {
    const selected = getSelectedInsurance(insurance);
    return selected.is_enabled;
  };

  const getInsuranceRate = (insurance: InsuranceWithRate) => {
    const selected = getSelectedInsurance(insurance);
    return selected.rate_percent;
  };

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Versicherungen
          {employeeAge && (
            <Badge variant="outline">Alter: {employeeAge} Jahre</Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Versicherung suchen (z.B. AHV, BVG, UVG...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Insurance Groups */}
        <TooltipProvider>
          <div className="space-y-6">
            {Object.entries(groupedInsurances).map(([category, categoryInsurances]) => (
              <div key={category} className="space-y-3">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-sm">
                    {categoryLabels[category as keyof typeof categoryLabels] || category}
                  </h4>
                  <Separator className="flex-1" />
                </div>

                <div className="grid gap-3">
                  {categoryInsurances.map((insurance) => {
                    const isEnabled = isInsuranceEnabled(insurance);
                    const currentRate = getInsuranceRate(insurance);
                    const isCustomRate = getSelectedInsurance(insurance).is_custom_rate;

                    return (
                      <div
                        key={insurance.id}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          isEnabled
                            ? 'bg-accent/50 border-accent'
                            : 'bg-muted/30 border-border'
                        }`}
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <Checkbox
                            checked={isEnabled}
                            onCheckedChange={(checked) =>
                              handleInsuranceToggle(insurance, checked as boolean)
                            }
                            disabled={insurance.is_mandatory}
                          />

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Label className="font-medium text-sm">
                                {insurance.name_german}
                              </Label>
                              {insurance.is_mandatory && (
                                <Badge variant="secondary" className="text-xs">
                                  Obligatorisch
                                </Badge>
                              )}
                              {isCustomRate && (
                                <Badge variant="outline" className="text-xs">
                                  Angepasst
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              {insurance.name_english}
                            </p>
                            {insurance.description && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {insurance.description}
                              </p>
                            )}
                          </div>

                          <Tooltip>
                            <TooltipTrigger>
                              <Info className="h-4 w-4 text-muted-foreground" />
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="max-w-xs">
                                <p className="font-medium">{insurance.name_german}</p>
                                <p className="text-sm">{insurance.description}</p>
                                <p className="text-xs mt-1">Code: {insurance.code}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </div>

                        {/* Rate Input */}
                        <div className="flex items-center gap-2 ml-4">
                          <div className="relative">
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              value={currentRate.toFixed(2)}
                              onChange={(e) => {
                                const newRate = parseFloat(e.target.value) || 0;
                                handleRateChange(insurance, newRate);
                              }}
                              className="w-20 text-right pr-6"
                              disabled={!isEnabled}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                              %
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </TooltipProvider>

        {/* Summary */}
        {selectedInsurances.filter(i => i.is_enabled).length > 0 && (
          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
              <Check className="h-4 w-4 text-green-600" />
              Gewählte Versicherungen
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {selectedInsurances
                .filter(i => i.is_enabled)
                .map(insurance => (
                  <div key={insurance.id} className="flex justify-between">
                    <span>{insurance.code}</span>
                    <span className="font-mono">{insurance.rate_percent.toFixed(2)}%</span>
                  </div>
                ))}
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between text-sm font-medium">
              <span>Total:</span>
              <span className="font-mono">
                {selectedInsurances
                  .filter(i => i.is_enabled)
                  .reduce((sum, i) => sum + i.rate_percent, 0)
                  .toFixed(2)}%
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};